<template>
  <router-view />
  <TheCssBreakpointDetector />
</template>

<script setup lang="tsx">
import { routeNames } from "@/routes";
import { TheCssBreakpointDetector, useAppNav } from "@modernary/mui/components/common";
import { useRoute } from "vue-router";

const route = useRoute();
const { setNavigationItems, setApplicationTitleComponent } = useAppNav();

const titleComponent = () => <div>AQMS</div>;
setApplicationTitleComponent(titleComponent);

// Nav items should be set one time during app initilization / config.
setNavigationItems([
  {
    label: "home",
    className: "",
    showLabel: false,
    items: [
      { label: "Home", name: routeNames.home },
      {
        label: "Organizations",
        name: routeNames.organization.organizations,
      },
      { label: "People", name: routeNames.person.people },
    ],
  },
  {
    // External
    label: "Form Submissions",
    className: "",
    showLabel: false,
    items: [{ label: "Electronic Forms", name: routeNames.electronicForm.electronicForms }],
  },
  {
    // External
    label: "Complaints",
    className: "",
    showLabel: false,
    items: [{ label: "File a Complaint", name: routeNames.complaint.new }],
  },
  {
    label: "Complaints",
    className: "",
    items: [
      { label: "Complaints", name: routeNames.complaint.complaints },
      {
        label: "Enforcement Actions",
        breadcrumbLabel: "Complaint Enforcement Actions",
        name: routeNames.complaintEnforcementAction.complaintEnforcementActions,
      },
      {
        label: "Investigations",
        breadcrumbLabel: "Complaint Investigations",
        name: routeNames.complaintInvestigation.complaintInvestigations,
      },
    ],
  },
  {
    label: "Form Submissions",
    className: "",
    items: [
      // { label: "Asbestos Forms", name: routeNames.electronicForm.asbestos },
      // { label: "Compliance Forms", name: routeNames.electronicForm.compliance },
      // { label: "Dust Control Permitting Forms", name: routeNames.electronicForm.dustControlPermitting },
      // { label: "Dust Class Registrations", name: routeNames.electronicForm.dustClass },
      // { label: "Dust Monitor Class Registrations", name: routeNames.electronicForm.dustMonitorClass },
      // { label: "Enforcement Forms", name: routeNames.electronicForm.enforcement },
      { label: "Open Burning Forms", name: routeNames.electronicForm.openBurning },
      // { label: "Source Testing Forms", name: routeNames.electronicForm.sourceTesting },
      // { label: "Stationary Source Permitting Forms", name: routeNames.electronicForm.stationarySourcePermitting },
    ],
  },
  {
    label: "Stationary Sources",
    className: "xl:break-before-column",
    items: [
      {
        label: "Compliance Forms",
        breadcrumbLabel: "Stationary Source Compliance Forms",
        name: routeNames.electronicForm.compliance,
      },
      {
        label: "Enforcement Actions",
        breadcrumbLabel: "Stationary Source Enforcement Actions",
        name: routeNames.stationarySourceEnforcementAction.stationarySourceEnforcementActions,
      },
      {
        label: "Enforcement Forms",
        breadcrumbLabel: "Stationary Source Enforcement Forms",
        name: routeNames.electronicForm.enforcement,
      },
      {
        label: "Permitting Forms",
        breadcrumbLabel: "Stationary Source Permitting Forms",
        name: routeNames.electronicForm.stationarySourcePermitting,
      },
      {
        label: "Source Testing Forms",
        breadcrumbLabel: "Stationary Source Testing Forms",
        name: routeNames.electronicForm.sourceTesting,
      },

      { label: "Stationary Sources", name: routeNames.stationarySource.stationarySources },
    ],
  },
  {
    label: "Asbestos",
    className: "",
    items: [
      {
        label: "Enforcement Actions",
        breadcrumbLabel: "Asbestos Enforcement Actions",
        name: routeNames.asbestosEnforcementAction.asbestosEnforcementActions,
      },
      {
        label: "Facilities",
        breadcrumbLabel: "Asbestos Facilities",
        name: routeNames.asbestosFacility.asbestosFacilities,
      },
      {
        label: "Forms",
        breadcrumbLabel: "Asbestos Forms",
        name: routeNames.electronicForm.asbestos,
      },
      {
        label: "Inspections",
        breadcrumbLabel: "Asbestos Inspections",
        name: routeNames.asbestosInspection.asbestosInspections,
      },
      {
        label: "Notifications",
        breadcrumbLabel: "Asbestos Notifications",
        name: routeNames.asbestosNotification.asbestosNotifications,
      },
      {
        label: "Projects",
        breadcrumbLabel: "Asbestos Projects",
        name: routeNames.asbestosProject.asbestosProjects,
      },
    ],
  },
  {
    label: "Vacant Lands",
    className: "",
    items: [
      {
        label: "Enforcement Actions",
        breadcrumbLabel: "Vacant Land Enforcement Actions",
        name: routeNames.vacantLandEnforcementAction.vacantLandEnforcementActions,
      },
      {
        label: "Inspections",
        breadcrumbLabel: "Vacant Land Inspections",
        name: routeNames.vacantLandInspection.vacantLandInspections,
      },
      {
        label: "Parcels",
        breadcrumbLabel: "Vacant Land Parcels",
        name: routeNames.vacantLandParcel.vacantLandParcels,
      },
    ],
  },
  {
    label: "Dust Control",
    className: "2xl:break-before-column",
    items: [
      {
        label: "Applications (Historical)",
        breadcrumbLabel: "Dust Applications (Historical)",
        name: routeNames.application.applications,
      },
      { label: "Construction Projects", name: routeNames.constructionProject.constructionProjects },
      {
        label: "Enforcement Actions",
        breadcrumbLabel: "Dust Enforcement Actions",
        name: routeNames.dustEnforcementAction.dustEnforcementActions,
      },
      { label: "Forms", breadcrumbLabel: "Dust Forms", name: routeNames.electronicForm.dustControlPermitting },

      {
        label: "Inspections",
        breadcrumbLabel: "Dust Inspections",
        name: routeNames.dustInspection.dustInspections,
      },
      {
        label: "Permits",
        breadcrumbLabel: "Dust Permits",
        name: routeNames.dustPermit.dustPermits,
      },
    ],
  },
  {
    label: "Dust Classes",
    className: "",
    items: [
      {
        label: "Cards",
        breadcrumbLabel: "Dust Cards",
        name: routeNames.dustCard.dustCards,
      },
      {
        label: "Classes",
        breadcrumbLabel: "Dust Classes",
        name: routeNames.dustClass.dustClasses,
      },
      { label: "Forms", breadcrumbLabel: "Dust Class Forms", name: routeNames.electronicForm.dustClass },
    ],
  },
  {
    label: "Dust Monitor Classes",
    className: "",
    items: [
      {
        label: "Cards",
        breadcrumbLabel: "Dust Monitor Cards",
        name: routeNames.dustMonitorCard.dustMonitorCards,
      },
      {
        label: "Classes",
        breadcrumbLabel: "Dust Monitor Classes",
        name: routeNames.dustMonitorClass.dustMonitorClasses,
      },
      { label: "Forms", breadcrumbLabel: "Dust Monitor Class Forms", name: routeNames.electronicForm.dustMonitorClass },
    ],
  },
  {
    label: "Finance",
    className: "3xl:break-before-column",
    items: [
      {
        label: "Invoices",
        name: routeNames.invoice.invoices,
      },
    ],
  },

  // Add routes to ./routes.tsx
  // In order to authorize route, must add policy to PolicyAccessConrols at Name: [AQMS2:Navigation Policy Access Controls]
  {
    label: "Temporary",
    className: "",
    items: [
      {
        label: "Users",
        name: routeNames.user.users,
      },
    ],
  },
]);
</script>
